import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GraduationCap, BedDouble, Activity, Building, UserX } from 'lucide-react';
import { format } from 'date-fns';

interface OverdueItem {
  staffName: string;
  trainingTitle: string;
  dueDate: string;
  daysOverdue: number;
}

interface UnbeddedClient {
  id: string;
  first_name: string;
  last_name: string;
  site_name?: string;
  updated_at: string;
}

interface AlertItem {
  id: string;
  title: string;
  description: string;
  type: string;
  site_name?: string;
  created_at: string;
}

interface ServiceBreakdown {
  [type: string]: number;
}

interface Props {
  overdueItems: OverdueItem[];
  unbeddedClients: UnbeddedClient[];
  serviceBreakdown: ServiceBreakdown;
  facilityAlerts: AlertItem[];
  clientAlerts: AlertItem[];
}

export const SummaryDetailSections = ({
  overdueItems,
  unbeddedClients,
  serviceBreakdown,
  facilityAlerts,
  clientAlerts,
}: Props) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Overdue Trainings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-red-600" />
            Overdue Trainings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {overdueItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No overdue trainings</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Staff</TableHead>
                  <TableHead className="text-xs">Training</TableHead>
                  <TableHead className="text-xs text-right">Overdue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overdueItems.slice(0, 10).map((item, i) => (
                  <TableRow key={i} className="h-9">
                    <TableCell className="py-1 text-sm font-medium">{item.staffName}</TableCell>
                    <TableCell className="py-1 text-sm text-muted-foreground">{item.trainingTitle}</TableCell>
                    <TableCell className="py-1 text-sm text-right text-destructive font-medium">
                      {item.daysOverdue}d
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Unbedded Clients */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BedDouble className="h-4 w-4 text-orange-600" />
            Unbedded Clients
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unbeddedClients.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No unbedded clients</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Facility</TableHead>
                  <TableHead className="text-xs text-right">Last Update</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unbeddedClients.slice(0, 10).map(client => (
                  <TableRow key={client.id} className="h-9">
                    <TableCell className="py-1 text-sm font-medium">
                      {client.first_name} {client.last_name}
                    </TableCell>
                    <TableCell className="py-1 text-sm text-muted-foreground">
                      {client.site_name || '—'}
                    </TableCell>
                    <TableCell className="py-1 text-sm text-right text-muted-foreground">
                      {format(new Date(client.updated_at), 'MMM dd')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Services Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-600" />
            Services Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(serviceBreakdown).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No services in this period</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(serviceBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
                    <span className="text-sm font-medium capitalize">
                      {type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm font-semibold text-blue-600">{count}</span>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts (combined facility + client) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Building className="h-4 w-4 text-amber-600" />
            Active Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {facilityAlerts.length === 0 && clientAlerts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No active alerts</p>
          ) : (
            <div className="space-y-3">
              {facilityAlerts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                    <Building className="h-3 w-3" /> Facility ({facilityAlerts.length})
                  </p>
                  {facilityAlerts.slice(0, 5).map(alert => (
                    <div key={alert.id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
                      <span className="text-sm truncate max-w-[200px]">{alert.title}</span>
                      <span className="text-xs text-muted-foreground">{alert.site_name || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {clientAlerts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                    <UserX className="h-3 w-3" /> Client ({clientAlerts.length})
                  </p>
                  {clientAlerts.slice(0, 5).map(alert => (
                    <div key={alert.id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
                      <span className="text-sm truncate max-w-[200px]">{alert.title}</span>
                      <span className="text-xs text-muted-foreground">{alert.site_name || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
